<template>
  <div>
    <b-card-body>
      <div class="row mx-md-n3 mx-1 mb-2 align-items-end">
        <div class="col-sm-6 col-12 mb-sm-0 mb-3">
          <b-form-group
            label="Nome ou Número"
            label-for="balancesSearchText"
            class="mb-0"
          >
            <b-form-input
              id="balancesSearchText"
              v-model="balancesSearchText"
              @keyup.enter="balancesSearchButtonPressed"
              placeholder="Procurar por..."
            />
          </b-form-group>
        </div>
        <div class="col-sm-auto col-6">
          <b-dropdown text="Filtros" class="mobile-btn-100">
            <b-dropdown-form>
              <b-form-group
                label="Apenas com saldo positivo"
              >
                <b-form-checkbox v-model="onlyPositiveBalances"></b-form-checkbox>
              </b-form-group>
              <b-form-group
                label="Armazém"
              >
                <div v-if="loadingWarehouses">
                  <loading-animation />
                </div>
                <model-list-select
                  :list="warehouses"
                  option-value="id"
                  option-text="name"
                  v-model="balancesSelectedWarehouse"
                  placeholder="Armazém"
                />
              </b-form-group>
              <b-form-group
                label="Endereço"
              >
                <div v-if="locationsFilter.loading">
                  <loading-animation />
                </div>
                <model-list-select
                  :list="locationsFilter.locations"
                  option-value="id"
                  option-text="name"
                  v-model="balancesSelectedLocation"
                  placeholder="Endereço"
                  @searchchange="locationsFilterChanged"
                  :filterPredicate="alwaysTrueFilterPredicate"
                />
              </b-form-group>
              <b-form-group
                label="Lote"
              >
                <div v-if="lotsFilter.loading">
                  <loading-animation />
                </div>
                <model-list-select
                  :list="lotsFilter.lots"
                  option-value="id"
                  option-text="lotNumber"
                  v-model="balancesSelectedLot"
                  placeholder="Digite para pesquisar"
                  @searchchange="searchLots"
                  :filterPredicate="alwaysTrueFilterPredicate"
                />
              </b-form-group>
            </b-dropdown-form>
          </b-dropdown>
        </div>
        <div class="col-xl-2 col-lg-2 col-md-2 col-sm-3 col-6">
          <button @click="balancesSearchButtonPressed" type="button" class="btn btn-primary mobile-btn-100">Buscar</button>
        </div>
      </div>

      <div v-if="balancesAppliedFilters.length" class="row mb-2 mx-0">
        <div v-for="(filter, index) in balancesAppliedFilters" class="col-auto px-1" :key="index">
          <h4>
            <span class="badge badge-warning">{{filter.label}} = {{filter.value}}</span>
          </h4>
        </div>
      </div>

      <div class="row mx-md-n3 mx-1">
        <b-col sm="12">
          <div v-if="loadingItemBalances" class="mb-5 pb-5">
            <loading-animation />
          </div>

          <div v-if="!loadingItemBalances && !itemBalances.length" class="mb-3">
            <div class="row bg-light mx-0 py-3">
              <div class="col text-center">
                Nenhum estoque encontrado para a pesquisa
              </div>
            </div>
          </div>

          <div v-if="!loadingItemBalances && itemBalances.length" class="mb-3">
            <div v-for="(item, index) in itemBalances" class="row py-3 border mx-0" :key="index"
              :class="{ 'bg-light': index % 2 != 0 }">
              <div class="col-xl-9 col-lg-8 col-md-8 col-sm-8 col-6">
                <div class="row">
                  <div class="col-12">
                    <span class="font-weight-bold">Número do Lote: </span>{{item.itemLot ? item.itemLot.lotNumber : ''}}
                  </div>
                  <div class="col-12">
                    <span class="font-weight-bold">Endereço: </span>{{item.location ? item.location.name : ''}}
                  </div>
                </div>
              </div>
              <div class="col-xl-3 col-lg-4 col-md-4 col-sm-4 col-6">
                <div class="row">
                  <div class="col-12">
                    <span class="font-weight-bold">Entradas: </span>{{item.entries}}
                  </div>
                  <div class="col-12">
                    <span class="font-weight-bold">Saídas: </span>{{item.exits}}
                  </div>
                  <div class="col-12">
                    <span class="font-weight-bold">Saldo: </span>{{(item.entries || 0) - (item.exits || 0)}}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <nav>
            <b-pagination
              align="center"
              :limit="10"
              :total-rows="balancesTotalRows"
              :per-page="balancesPerPage"
              prev-text="Anterior"
              next-text="Próximo"
              number-of-page="numberOfPages"
              v-model="balancesCurrentPage"
              @change="balancesPageChanged"
              :disabled="loadingItemBalances"/>
          </nav>
        </b-col>
      </div>
    </b-card-body>
  </div>
</template>

<script>
  import { LoadingAnimation } from '@/components/loaders'
  import { httpClient } from '@/service';
  import shared from '@/shared/shared'
  import { ModelListSelect } from 'vue-search-select'

  export default {
    name: 'item-balances',
    components: {
      LoadingAnimation,
      ModelListSelect
    },

    props: {
      item: {
        type: Object,
        required: true
      },
      loadingWarehouses: {
        type: Boolean,
        required: true
      },
      warehouses: {
        type: Array,
        required: true
      },
      alwaysTrueFilterPredicate: {
        type: Function,
        required: true
      },
      locationsFilter: {
        type: Object,
        required: true
      },
      lotsFilter: {
        type: Object,
        required: true
      },
      searchLocations: {
        type: Function,
        required: true
      },
      searchLots: {
        type: Function,
        required: true
      }
    },

    data () {
      return {
        loadingItemBalances: false,
        itemBalances: [],
        balancesCurrentPage: 1,
        balancesPerPage: 10,
        balancesTotalRows: 10,
        balancesSearchText: null,
        onlyPositiveBalances: false,
        balancesSelectedLot: {},
        balancesSelectedWarehouse: {},
        balancesSelectedLocation: {},
        itemBalancesSearched: false,
      }
    },

    beforeMount: function () {
      this.balancesSearchText = shared.getLocalStorageString('items/show/Balances/balancesSearchText', this.balancesSearchText);
      this.balancesSelectedWarehouse = shared.getLocalStorageObj('items/show/Balances/balancesSelectedWarehouse', this.balancesSelectedWarehouse);
      this.balancesSelectedLocation = shared.getLocalStorageObj('items/show/Balances/balancesSelectedLocation', this.balancesSelectedLocation);
      this.balancesSelectedLot = shared.getLocalStorageObj('items/show/Balances/balancesSelectedLot', this.balancesSelectedLot);
      this.onlyPositiveBalances = shared.getLocalStorageString('items/show/Balances/onlyPositiveBalances', this.onlyPositiveBalances);
    },

    computed: {
      balancesAppliedFilters() {
        let appliedFilters = [];

        if (this.balancesSearchText && this.balancesSearchText.length) {
          appliedFilters.push({ label: 'Nome ou Número', value: this.balancesSearchText });
        }

        if (this.onlyPositiveBalances) {
          appliedFilters.push({ label: 'Apenas com saldo positivo', value: 'Sim' });
        }

        if (this.balancesSelectedWarehouse && this.balancesSelectedWarehouse.id) {
          appliedFilters.push({ label: 'Armazém', value: this.balancesSelectedWarehouse.name });
        }

        if (this.balancesSelectedLocation && this.balancesSelectedLocation.id) {
          appliedFilters.push({ label: 'Endereço', value: this.balancesSelectedLocation.name });
        }

        if (this.balancesSelectedLot && this.balancesSelectedLot.id) {
          appliedFilters.push({ label: 'Lote', value: this.balancesSelectedLot.lotNumber });
        }

        return appliedFilters;
      },
    },

    methods: {
      tabChanged() {
        if (!this.itemBalancesSearched) {
          this.searchItemBalances();
          this.itemBalancesSearched = true;
        }
      },

      balancesSearchButtonPressed() {
        this.balancesCurrentPage = 1;
        this.searchItemBalances();
      },

      balancesPageChanged(page) {
        this.balancesCurrentPage = page;
        this.searchItemBalances();
      },

      searchItemBalances() {
        if (!this.loadingItemBalances) {
          this.loadingItemBalances = true

          let conditions = [
            {
              field: 'itemLot.item.id',
              value: this.item.id
            }
          ];

          if (this.balancesSearchText && this.balancesSearchText.length) {
            conditions.push({
              logicalOperator: 'OR',
              conditions: [
                {
                  field: 'location.name',
                  conditionalOperator: 'LIKE_START',
                  value: this.balancesSearchText
                },
                {
                  field: 'itemLot.lotNumber',
                  conditionalOperator: 'LIKE_START',
                  value: this.balancesSearchText
                }
              ]
            });
          }

          shared.setLocalStorageString('items/show/Balances/balancesSearchText', this.balancesSearchText);

          if (this.balancesSelectedWarehouse && this.balancesSelectedWarehouse.id) {
            conditions.push({
              field: 'location.stockLocale.id',
              value: this.balancesSelectedWarehouse.id
            });
          }

          shared.setLocalStorageObj('items/show/Balances/balancesSelectedWarehouse', this.balancesSelectedWarehouse);

          if (this.balancesSelectedLocation && this.balancesSelectedLocation.id) {
            conditions.push({
              field: 'location.id',
              value: this.balancesSelectedLocation.id
            });
          }

          shared.setLocalStorageObj('items/show/Balances/balancesSelectedLocation', this.balancesSelectedLocation);

          if (this.balancesSelectedLot && this.balancesSelectedLot.id) {
            conditions.push({
              field: 'itemLot.id',
              value: this.balancesSelectedLot.id
            });
          }

          shared.setLocalStorageObj('items/show/Balances/balancesSelectedLot', this.balancesSelectedLot);

          if (this.onlyPositiveBalances) {
            conditions.push({
              field: 'entries',
              conditionalOperator: 'GREATER_THAN',
              expressionValue: 'exits'
            });
          }

          shared.setLocalStorageString('items/show/Balances/onlyPositiveBalances', this.onlyPositiveBalances);

          httpClient
          .post(`${process.env.VUE_APP_API_URL}balances/condition?page=${this.balancesCurrentPage - 1}&size=${this.balancesPerPage}`, {
            conditions: conditions
          })
          .then(({ data }) => {
            this.itemBalances = data.data.content;
            this.balancesTotalRows = data.data.totalElements;

            this.loadingItemBalances = false
          })
          .catch((error) => {
            if (error.message) {
              this.$notify.error(error)
            }
            this.itemBalances = [];
            this.loadingItemBalances = false
          });
        }
      },

      locationsFilterChanged($event) {
        this.searchLocations($event, conditions => {
          if (this.balancesSelectedWarehouse && this.balancesSelectedWarehouse.id) {
            conditions.push({
              field: 'stockLocale.id',
              value: this.balancesSelectedWarehouse.id
            });
          }

          return conditions;
        });
      }
    },

    watch: {
      'balancesSelectedWarehouse': function(newVal, oldVal) {
        if (!shared.entitiesAreEqual(newVal, oldVal)) {
          this.locationsFilter.locations = [];
        }
      }
    }
  }
</script>

<style scoped>
</style>
